import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import {
  QueryClient,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import { ValueOf } from 'type-fest';

import {
  TaskStatusObject,
  TaskStatusUpdateMessage,
  TaskStatusCacheEntry,
  RelatedEntityPks,
  TaskEntityKeys,
  RelatedEntityType,
  TaskEntityType,
  TaskPipeline,
  AsyncTasks,
  FetchTaskStatusesResponse,
  TaskStatus,
  TaskType,
  TaskResolution,
  DEFAULT_TASK_ID,
} from '@frontend/api';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useFeatureFlagValue } from 'store/session/hooks';

import { toCamelCase } from 'utils/stringUtils';

import { useSubscribeWebsocket } from '../useSubscribeWebsocket';

type EntityKey = string;

export const getEntityTaskStatusQueryKey = (entityKey: EntityKey) => [
  'entityTaskStatus',
  entityKey,
];

const getEntityTaskStatus = (
  entityKey: EntityKey,
  queryClient: QueryClient,
): TaskStatusCacheEntry | undefined => {
  const queryKey = getEntityTaskStatusQueryKey(entityKey);
  return queryClient.getQueryData<TaskStatusCacheEntry | undefined>(queryKey);
};

const setEntityTaskStatus = (
  entityKey: EntityKey,
  status: TaskStatusCacheEntry,
  queryClient: QueryClient,
) => {
  const queryKey = getEntityTaskStatusQueryKey(entityKey);
  queryClient.setQueryData(queryKey, status);
};

export const getTaskEntityKeysQueryKey = (
  entityType: ValueOf<typeof RelatedEntityType>,
  entityPk: string | number,
) => ['taskEntityKeys', entityType, String(entityPk)];

const getTaskEntityKeys = (
  entityType: ValueOf<typeof RelatedEntityType>,
  entityPk: string | number,
  queryClient: QueryClient,
) => {
  const queryKey = getTaskEntityKeysQueryKey(entityType, entityPk);
  return queryClient.getQueryData<TaskEntityKeys>(queryKey);
};

const setTaskEntityKeys = (
  entityType: ValueOf<typeof RelatedEntityType>,
  entityPk: string | number,
  taskEntityKeys: TaskEntityKeys,
  queryClient: QueryClient,
) => {
  const queryKey = getTaskEntityKeysQueryKey(entityType, entityPk);
  queryClient.setQueryData(queryKey, taskEntityKeys);
};

// Helper functions for handling nested task structure
const getTaskId = (update: TaskStatusUpdateMessage): string =>
  update.taskKey ?? DEFAULT_TASK_ID;

export const mergeTaskStatusUpdates = (
  currentStatus: TaskStatusCacheEntry,
  updates: TaskStatusUpdateMessage[],
): [TaskStatusCacheEntry, boolean] => {
  let hasChanges = false;
  let newStatus: TaskStatusCacheEntry = currentStatus;
  const modifiedTasks = new Set<TaskType>();

  for (const update of updates) {
    const { task, stamp } = update;
    const taskId = getTaskId(update);

    const currentTaskEntries = currentStatus[task];
    const currentTaskEntry = currentTaskEntries?.[taskId];

    if (!currentTaskEntry || stamp > currentTaskEntry.stamp) {
      // Only create root object copy on first change
      if (!hasChanges) {
        newStatus = { ...currentStatus };
        hasChanges = true;
      }

      // Only copy this specific task's entries if we haven't already
      if (!modifiedTasks.has(task)) {
        newStatus[task] = currentTaskEntries ? { ...currentTaskEntries } : {};
        modifiedTasks.add(task);
      }

      const newTaskStatusObject: TaskStatusObject = {
        status: update.status,
        resolution: update.resolution,
        pipeline: update.pipeline,
        stamp,
      };
      newStatus[task][taskId] = newTaskStatusObject;
    }
  }

  return [newStatus, hasChanges];
};

export const mergeTaskStatusCacheEntries = (
  entryA: TaskStatusCacheEntry,
  entryB: TaskStatusCacheEntry,
): [TaskStatusCacheEntry, boolean] => {
  let hasChanges = false;
  const merged: TaskStatusCacheEntry = {};

  // Deep copy entryA
  for (const [task, taskEntries] of Object.entries(entryA ?? {})) {
    merged[task] = { ...taskEntries };
  }

  // Merge entryB into merged
  for (const [task, taskEntriesB] of Object.entries(entryB)) {
    if (!merged[task]) {
      merged[task] = {};
    }

    for (const [taskId, statusB] of Object.entries(taskEntriesB)) {
      const statusA = merged[task][taskId];

      if (!statusA || statusB.stamp > statusA.stamp) {
        merged[task][taskId] = statusB;
        if (!statusA || statusB.stamp !== statusA.stamp) {
          hasChanges = true;
        }
      }
    }
  }

  return [merged, hasChanges];
};

const copyTaskEntityKeys = (
  currentTaskEntitykeys?: TaskEntityKeys,
): TaskEntityKeys => {
  return {
    [TaskEntityType.PART_FILE]: (
      currentTaskEntitykeys?.[TaskEntityType.PART_FILE] ?? []
    ).slice(),
    [TaskEntityType.COMPONENT]: (
      currentTaskEntitykeys?.[TaskEntityType.COMPONENT] ?? []
    ).slice(),
    [TaskEntityType.QUOTE]: (
      currentTaskEntitykeys?.[TaskEntityType.QUOTE] ?? []
    ).slice(),
  };
};

type TaskEntityKeysInvertedIndexKey = `${ValueOf<typeof RelatedEntityType>}.${
  | string
  | number}`;
type TaskEntityKeysInvertedIndex = Record<
  TaskEntityKeysInvertedIndexKey,
  TaskEntityKeys
>;

/**
 * Add task entity key to an inverted index mapping related entities (quotes, parts, components,
 * etc.) to the task entity keys (part file uuid, component uuid, etc.)that are related to them.
 *
 * NOTE: this is a merge, not a replace. If a part file is deleted we will not purge that from
 * the inverted index cache - this is fine because if it is deleted, it is no longer on the
 * page, and thus the resource does not need to respond to updates to it.
 * @param invertedIndex
 * @param relatedEntityPks
 */
export const updateRelatedEntityPksInvertedIndex = (
  invertedIndex: TaskEntityKeysInvertedIndex,
  relatedEntityPks: RelatedEntityPks,
) => {
  const arr = [
    {
      entityType: RelatedEntityType.QUOTE,
      entityPks: relatedEntityPks.quoteIds ?? [],
    },
    {
      entityType: RelatedEntityType.PART_FILE_TO_QUOTE,
      entityPks: relatedEntityPks.partFileToQuoteQuoteIds ?? [],
    },
    {
      entityType: RelatedEntityType.PART_MASTER,
      entityPks: relatedEntityPks.partMasterUuids ?? [],
    },
    {
      entityType: RelatedEntityType.PART,
      entityPks: relatedEntityPks.partUuids ?? [],
    },
    {
      entityType: RelatedEntityType.COMPONENT,
      entityPks: relatedEntityPks.componentIds ?? [],
    },
    {
      entityType: RelatedEntityType.QUOTE_ITEM,
      entityPks: relatedEntityPks.quoteItemIds ?? [],
    },
  ];
  for (const { entityType, entityPks } of arr) {
    for (const entityPk of entityPks) {
      const invertedIndexKey =
        `${entityType}.${entityPk}` as TaskEntityKeysInvertedIndexKey;
      if (!invertedIndex[invertedIndexKey]) {
        invertedIndex[invertedIndexKey] = copyTaskEntityKeys();
      }
      invertedIndex[invertedIndexKey][relatedEntityPks.entityType].push(
        relatedEntityPks.entityKey,
      );
    }
  }
};

export const mergeTaskEntityKeys = ({
  currentTaskEntityKeys,
  taskEntityUpdates,
}: {
  currentTaskEntityKeys?: TaskEntityKeys;
  taskEntityUpdates: TaskEntityKeys;
}): [TaskEntityKeys, boolean] => {
  const newTaskEntityKeys = copyTaskEntityKeys(currentTaskEntityKeys);
  let hasChanges = false;

  const arr = [
    {
      entityType: TaskEntityType.PART_FILE,
      entityKeys: taskEntityUpdates[TaskEntityType.PART_FILE] ?? [],
    },
    {
      entityType: TaskEntityType.COMPONENT,
      entityKeys: taskEntityUpdates[TaskEntityType.COMPONENT] ?? [],
    },
    {
      entityType: TaskEntityType.QUOTE,
      entityKeys: taskEntityUpdates[TaskEntityType.QUOTE] ?? [],
    },
  ];

  for (const { entityType, entityKeys } of arr) {
    for (const entityKey of entityKeys) {
      if (!newTaskEntityKeys[entityType].includes(entityKey)) {
        newTaskEntityKeys[entityType].push(entityKey);
        hasChanges = true;
      }
    }
  }

  return [newTaskEntityKeys, hasChanges];
};

/**
 * Predicate function to determine if a task status update should be applied to an auto-watch
 * callback.
 */
export const autoWatchTaskStatusUpdatePredicate = ({
  update,
  taskEntityKeys,
  descriptor,
}: {
  update: TaskStatusUpdateMessage;
  taskEntityKeys: TaskEntityKeys;
  descriptor: StatusPredicate;
}) => {
  if (
    !taskEntityKeys[update.relatedEntityPks.entityType].includes(
      update.relatedEntityPks.entityKey,
    )
  ) {
    return false;
  }
  if (descriptor.tasks) {
    if (!descriptor.tasks.includes(update.task)) {
      return false;
    }
  }
  if (descriptor.pipelines) {
    if (!update.pipeline) {
      return false;
    }
    if (!descriptor.pipelines.includes(update.pipeline)) {
      return false;
    }
  }
  if (descriptor.statuses) {
    if (!descriptor.statuses.includes(update.status)) {
      return false;
    }
  }
  if (descriptor.resolutions) {
    if (!update.resolution) {
      return false;
    }
    if (!descriptor.resolutions.includes(update.resolution)) {
      return false;
    }
  }
  return true;
};

type OptimizedDescriptor = {
  taskSet?: Set<ValueOf<typeof AsyncTasks>>;
  pipelineSet?: Set<ValueOf<typeof TaskPipeline>>;
  statusSet?: Set<ValueOf<typeof TaskStatus>>;
  resolutionSet?: Set<ValueOf<typeof TaskResolution>>;
};

/**
 * Get optimized Sets for a descriptor
 */
export const getOptimizedDescriptor = (
  descriptor: StatusPredicate,
): OptimizedDescriptor => {
  return {
    taskSet: descriptor.tasks ? new Set(descriptor.tasks) : undefined,
    pipelineSet: descriptor.pipelines
      ? new Set(descriptor.pipelines)
      : undefined,
    statusSet: descriptor.statuses ? new Set(descriptor.statuses) : undefined,
    resolutionSet: descriptor.resolutions
      ? new Set(descriptor.resolutions)
      : undefined,
  };
};

/**
 * Optimized predicate function to determine if a status object matches criteria.
 * Uses Set-based lookups for O(1) performance and handles both raw and optimized descriptors.
 */
export const autoLoadingStatusPredicate = ({
  task,
  statusObject,
  descriptor,
}: {
  task: TaskType;
  statusObject: TaskStatusObject;
  descriptor: OptimizedDescriptor;
}) => {
  const { taskSet, pipelineSet, statusSet, resolutionSet } = descriptor;

  // Early return optimizations with Set-based lookups
  if (taskSet && !taskSet.has(task)) {
    return false;
  }
  if (pipelineSet) {
    if (!statusObject.pipeline || !pipelineSet.has(statusObject.pipeline)) {
      return false;
    }
  }
  if (statusSet && !statusSet.has(statusObject.status)) {
    return false;
  }
  if (resolutionSet) {
    if (
      !statusObject.resolution ||
      !resolutionSet.has(statusObject.resolution)
    ) {
      return false;
    }
  }
  return true;
};

/**
 * Determine if any related entity status is loading that is applicable to the descriptor.
 */
export const isAnyRelatedEntityLoading = ({
  relatedEntityStatuses,
  descriptor,
}: {
  relatedEntityStatuses: UseQueryResult<
    TaskStatusCacheEntry | undefined,
    unknown
  >[];
  descriptor: OptimizedDescriptor;
}) => {
  for (const statusQueryResult of relatedEntityStatuses) {
    if (!statusQueryResult.data) continue;

    for (const [task, taskEntries] of Object.entries(statusQueryResult.data)) {
      for (const statusObject of Object.values(taskEntries)) {
        if (
          autoLoadingStatusPredicate({
            task,
            statusObject,
            descriptor,
          })
        ) {
          return true; // Early return on first match
        }
      }
    }
  }
  return false;
};

/**
 * Determines if the surrounding task entity statuses to a related entity satisfy the predicate
 * provided.
 */
const evaluateSurroundingTaskEntityStatusesPredicate = ({
  surroundingTaskEntityStatuses,
  predicate,
}: {
  surroundingTaskEntityStatuses: Record<
    ValueOf<typeof TaskEntityType>,
    Record<EntityKey, TaskStatusCacheEntry>
  >;
  predicate: SurroundingStatusesPredicate;
}) => {
  const allStatuses = [
    ...Object.values(surroundingTaskEntityStatuses[TaskEntityType.PART_FILE]),
    ...Object.values(surroundingTaskEntityStatuses[TaskEntityType.COMPONENT]),
    ...Object.values(surroundingTaskEntityStatuses[TaskEntityType.QUOTE]),
  ];
  const optimizedDescriptor = getOptimizedDescriptor(predicate);
  let reduction = predicate.aggregate === 'all' ? true : false;
  for (const status of allStatuses) {
    for (const [task, taskEntries] of Object.entries(status)) {
      for (const statusObject of Object.values(taskEntries)) {
        // first check if the task or pipeline is applicable
        const isApplicable = autoLoadingStatusPredicate({
          task,
          statusObject,
          descriptor: {
            pipelineSet: optimizedDescriptor.pipelineSet,
            taskSet: optimizedDescriptor.taskSet,
          },
        });
        if (isApplicable) {
          // now apply the actual predicate to statuses and resolutions
          const matchesPredicate = autoLoadingStatusPredicate({
            task,
            statusObject,
            descriptor: {
              statusSet: optimizedDescriptor.statusSet,
              resolutionSet: optimizedDescriptor.resolutionSet,
            },
          });
          if (!matchesPredicate && predicate.aggregate === 'all') {
            return false;
          } else if (matchesPredicate && predicate.aggregate === 'any') {
            return true;
          } else if (matchesPredicate && predicate.aggregate === 'all') {
            reduction = reduction && matchesPredicate;
          }
        }
      }
    }
  }
  return reduction;
};

type TaskStatusCallback = ({
  updateMessages,
  previousState,
  nextState,
}: {
  updateMessages: TaskStatusUpdateMessage[];
  previousState: Record<EntityKey, TaskStatusCacheEntry>;
  nextState: Record<EntityKey, TaskStatusCacheEntry>;
}) => void;

type AutoWatchTaskStatusCallback = ({
  updateMessages,
  taskEntityStatuses,
  previousState,
  nextState,
}: {
  updateMessages: TaskStatusUpdateMessage[];
  taskEntityStatuses: Record<
    ValueOf<typeof TaskEntityType>,
    Record<EntityKey, TaskStatusCacheEntry>
  >;
  previousState: Record<EntityKey, TaskStatusCacheEntry>;
  nextState: Record<EntityKey, TaskStatusCacheEntry>;
}) => void;

type StatusPredicate = {
  pipelines?: ValueOf<typeof TaskPipeline>[];
  tasks?: ValueOf<typeof AsyncTasks>[];
  statuses?: ValueOf<typeof TaskStatus>[];
  resolutions?: ValueOf<typeof TaskResolution>[];
};

type SurroundingStatusesPredicate = StatusPredicate & {
  // if all, all applicable surrounding statuses must match the predicate,
  // if any, any applicable surrounding statuses must match the predicate
  aggregate: 'all' | 'any';
};

export type AutoWatchTasksDescriptor = StatusPredicate & {
  name: string; // use this as an additional unique string for caching purposes
  sourceEntityType: ValueOf<typeof RelatedEntityType>;
  sourceEntityPk: string | number;
  surroundingStatusesPredicate?: SurroundingStatusesPredicate;
};

type AutoWatchCallback = {
  callback: AutoWatchTaskStatusCallback;
  descriptor: AutoWatchTasksDescriptor;
};

type TaskStatusContextType = {
  registerCallback: (callbackKey: string, callback: TaskStatusCallback) => void;
  unregisterCallback: (callbackKey: string) => void;
  registerAutoWatchCallback: (callback: AutoWatchCallback) => void;
  unregisterAutoWatchCallback: (descriptor: AutoWatchTasksDescriptor) => void;
  updateStateWithFetchedStatuses: (
    statusesResponse: FetchTaskStatusesResponse,
  ) => void;
  handleWebsocketMessage: (message: TaskStatusWebsocketMessage) => void;
};

const TaskStatusContext = createContext<TaskStatusContextType | null>(null);

type TaskStatusWebsocketMessage = {
  event: 'file_status_update';
  updates: TaskStatusUpdateMessage[];
  stamp: number; // unix timestamp
};

export const TaskStatusProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const callbacksRef = useRef<Record<string, TaskStatusCallback>>({});
  const autoWatchCallbacksRef = useRef<Record<string, AutoWatchCallback>>({});
  const queryClient = useQueryClient();

  /**
   * This function is used to register a callback for a given callback key. These callbacks
   * will be run every time a new task status update message is received.
   *
   * NOTE: make sure the callback key is unique for the application, otherwise the function will
   * be overwritten by other callbacks. It is okay to have multiple components register the same
   * callback function - there are almost no performance penalties with this other than a few
   * redundant useEffect calls - e.g. employing the useLiveTableResults hook in multiple
   * components. However, make sure each unique side effect you want to run is ensured to have
   * a unique key. Think of the callbackKey a lot like how you construct a key for a
   * react-query.
   */
  const registerCallback = useCallback(
    (callbackKey: string, callback: TaskStatusCallback) => {
      callbacksRef.current[callbackKey] = callback;
    },
    [],
  );

  const unregisterCallback = useCallback((callbackKey: string) => {
    delete callbacksRef.current[callbackKey];
  }, []);

  /**
   * If anything changes that is tied to the resources in the descriptors, run the callback
   * function.
   * NOTE: Callback will only run if any applicable update messages are found based on the
   *  parameters in the descriptor.
   * NOTE: This is useful for when you do not want to keep track of all the related resources
   *  manualy with selectors - this will just know what messages to respond to if it is
   *  associated with the host resource and task array.
   */
  const registerAutoWatchCallback = useCallback(
    ({ callback, descriptor }: AutoWatchCallback) => {
      const invertedIndexKey = `${descriptor.name}.${descriptor.sourceEntityType}.${descriptor.sourceEntityPk}`;
      autoWatchCallbacksRef.current[invertedIndexKey] = {
        callback,
        descriptor,
      };
    },
    [],
  );

  const unregisterAutoWatchCallback = useCallback(
    (descriptor: AutoWatchTasksDescriptor) => {
      const invertedIndexKey = `${descriptor.name}.${descriptor.sourceEntityType}.${descriptor.sourceEntityPk}`;
      delete autoWatchCallbacksRef.current[invertedIndexKey];
    },
    [],
  );

  /**
   * Use this to register the responses from the initialization endpoint
   */
  const updateStateWithFetchedStatuses = useCallback(
    (statusesResponse: FetchTaskStatusesResponse) => {
      const allStatuses = statusesResponse[TaskEntityType.PART_FILE].concat(
        statusesResponse[TaskEntityType.COMPONENT],
      );
      for (const statusEntry of allStatuses) {
        const { entityKey, status } = statusEntry;
        const currentStatus = getEntityTaskStatus(entityKey, queryClient);
        // updating status cache on page load
        const [newStatus, hasChanges] = mergeTaskStatusCacheEntries(
          currentStatus ?? {},
          status,
        );

        if (hasChanges) {
          setEntityTaskStatus(entityKey, newStatus, queryClient);
        }
      }

      for (const taskEntityKeysWithMeta of statusesResponse.taskEntityKeys) {
        const {
          entityType: invertedIndexEntityType,
          entityPk: invertedIndexEntityPk,
        } = taskEntityKeysWithMeta;
        // updating inverted index of task entity keys on page load to enable auto watching
        const [newTaskEntityKeys, hasChanges] = mergeTaskEntityKeys({
          currentTaskEntityKeys: getTaskEntityKeys(
            invertedIndexEntityType,
            invertedIndexEntityPk,
            queryClient,
          ),
          taskEntityUpdates: taskEntityKeysWithMeta,
        });
        if (hasChanges) {
          setTaskEntityKeys(
            invertedIndexEntityType,
            invertedIndexEntityPk,
            newTaskEntityKeys,
            queryClient,
          );
        }
      }
    },
    [queryClient],
  );

  const handleWebsocketMessage = useCallback(
    (message: TaskStatusWebsocketMessage) => {
      const rawUpdates = message.updates;

      // need to convert raw task in message to camelCase since the task status is used as a key
      // in the statusObject - which is auto converted to camelCase in the api layer
      const updates: TaskStatusUpdateMessage[] = rawUpdates.map((update) => {
        const { task, ...rest } = update;
        return {
          ...rest,
          task: toCamelCase(task),
        };
      });

      // organize updates by entity key for processing
      const updatesByEntityKey = updates.reduce<
        Record<
          EntityKey,
          {
            messages: TaskStatusUpdateMessage[];
            relatedEntityPks: RelatedEntityPks;
          }
        >
      >((acc, update) => {
        if (!acc[update.entityKey]) {
          acc[update.entityKey] = {
            messages: [],
            relatedEntityPks: update.relatedEntityPks,
          };
        }
        acc[update.entityKey].messages.push(update);
        return acc;
      }, {});

      // now process each set of updates to get the aggregated next state
      const previousState: Record<EntityKey, TaskStatusCacheEntry> = {};
      const nextState: Record<EntityKey, TaskStatusCacheEntry> = {};
      const cacheUpdates: Array<{
        entityKey: EntityKey;
        newStatus: TaskStatusCacheEntry;
      }> = [];
      const tempInvertedIndex: Record<
        TaskEntityKeysInvertedIndexKey,
        TaskEntityKeys
      > = {};

      for (const entityKey in updatesByEntityKey) {
        const updates = updatesByEntityKey[entityKey].messages;
        const currentStatus = getEntityTaskStatus(entityKey, queryClient);

        const [newStatus, hasChanges] = mergeTaskStatusUpdates(
          currentStatus ?? {},
          updates,
        );
        if (currentStatus) {
          previousState[entityKey] = currentStatus;
        }
        nextState[entityKey] = newStatus;
        if (hasChanges) {
          // Batch cache updates instead of updating immediately
          cacheUpdates.push({ entityKey, newStatus });
        }

        // update temp inverted index
        // upon receiving a new message, update the inverted index in case new part files
        // or other related entity pks have been added.
        const relatedEntityPks = updatesByEntityKey[entityKey].relatedEntityPks;
        updateRelatedEntityPksInvertedIndex(
          tempInvertedIndex,
          relatedEntityPks,
        );
      }

      // now merge temp inverted index with existing inverted index
      for (const [invertedIndexKey, taskEntityUpdates] of Object.entries(
        tempInvertedIndex,
      )) {
        const [entityType, entityPk] = invertedIndexKey.split('.') as [
          ValueOf<typeof RelatedEntityType>,
          string | number,
        ];
        const [newTaskEntityKeys, hasChanges] = mergeTaskEntityKeys({
          currentTaskEntityKeys: getTaskEntityKeys(
            entityType,
            entityPk,
            queryClient,
          ),
          taskEntityUpdates,
        });
        if (hasChanges) {
          // TODO: should we bulk set?
          setTaskEntityKeys(
            entityType,
            entityPk,
            newTaskEntityKeys,
            queryClient,
          );
        }
      }

      // Batch all cache updates to minimize React Query re-renders
      for (const { entityKey, newStatus } of cacheUpdates) {
        // TODO: should we do a bulk set here?
        setEntityTaskStatus(entityKey, newStatus, queryClient);
      }

      // now enact callbacks
      for (const [callbackKey, callback] of Object.entries(
        callbacksRef.current,
      )) {
        try {
          callback({
            updateMessages: updates,
            previousState,
            nextState,
          });
        } catch (error) {
          console.error(`Error in callback ${callbackKey}:`, error);
        }
      }

      // now run auto-watch callbacks
      for (const { callback, descriptor } of Object.values(
        autoWatchCallbacksRef.current,
      )) {
        const taskEntityKeys = getTaskEntityKeys(
          descriptor.sourceEntityType,
          descriptor.sourceEntityPk,
          queryClient,
        );
        if (taskEntityKeys) {
          const applicableUpdates = updates.filter((update) =>
            autoWatchTaskStatusUpdatePredicate({
              update,
              taskEntityKeys,
              descriptor,
            }),
          );
          // only run callback if applicable updates are found
          if (applicableUpdates.length > 0) {
            // now collect the surrounding statuses and provide that to the callback as well
            const partFileStatus: Record<EntityKey, TaskStatusCacheEntry> = {};
            for (const partFileEntityKey of taskEntityKeys?.[
              TaskEntityType.PART_FILE
            ] ?? []) {
              const status = getEntityTaskStatus(
                partFileEntityKey,
                queryClient,
              );
              if (status) {
                partFileStatus[partFileEntityKey] = status;
              }
            }
            const componentStatus: Record<EntityKey, TaskStatusCacheEntry> = {};
            for (const componentEntityKey of taskEntityKeys?.[
              TaskEntityType.COMPONENT
            ] ?? []) {
              const status = getEntityTaskStatus(
                componentEntityKey,
                queryClient,
              );
              if (status) {
                componentStatus[componentEntityKey] = status;
              }
            }
            const quoteStatus: Record<EntityKey, TaskStatusCacheEntry> = {};
            for (const quoteEntityKey of taskEntityKeys?.[
              TaskEntityType.QUOTE
            ] ?? []) {
              const status = getEntityTaskStatus(quoteEntityKey, queryClient);
              if (status) {
                quoteStatus[quoteEntityKey] = status;
              }
            }
            const taskEntityStatuses = {
              [TaskEntityType.PART_FILE]: partFileStatus,
              [TaskEntityType.COMPONENT]: componentStatus,
              [TaskEntityType.QUOTE]: quoteStatus,
            };
            if (descriptor.surroundingStatusesPredicate) {
              // determine if there are any surrounding statuses that do not match the predicate
              // if so, do not enact callback
              const isApplicable =
                evaluateSurroundingTaskEntityStatusesPredicate({
                  surroundingTaskEntityStatuses: taskEntityStatuses,
                  predicate: descriptor.surroundingStatusesPredicate,
                });
              if (isApplicable) {
                callback({
                  updateMessages: applicableUpdates,
                  taskEntityStatuses,
                  previousState,
                  nextState,
                });
              }
            } else {
              callback({
                updateMessages: applicableUpdates,
                taskEntityStatuses,
                previousState,
                nextState,
              });
            }
          }
        }
      }
    },
    [queryClient],
  );

  useSubscribeWebsocket('task_status', handleWebsocketMessage, 500);

  return (
    <TaskStatusContext.Provider
      value={{
        registerCallback,
        unregisterCallback,
        registerAutoWatchCallback,
        unregisterAutoWatchCallback,
        updateStateWithFetchedStatuses,
        handleWebsocketMessage, // exposing for test convenience
      }}
    >
      {children}
    </TaskStatusContext.Provider>
  );
};

export const useTaskStatusTracking = () => {
  const context = useContext(TaskStatusContext);
  if (!context) {
    throw new Error(
      'useTaskStatusTracking must be used within a TaskStatusProvider',
    );
  }
  return context;
};

// react-lifecyle safe selectors
export const useEntityTaskStatus = (entityKey: EntityKey) => {
  const queryClient = useQueryClient();
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryClient?
    queryKey: getEntityTaskStatusQueryKey(entityKey),
    queryFn: () => getEntityTaskStatus(entityKey, queryClient),
    staleTime: 0, // Always fetch fresh data
  });
};

const useGetTaskEntityKeys = ({
  entityType,
  entityPk,
  shouldFetch,
  queryClient,
}: {
  entityType: ValueOf<typeof RelatedEntityType>;
  entityPk: string | number;
  shouldFetch: boolean;
  queryClient: QueryClient;
}) => {
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryClient?
    queryKey: getTaskEntityKeysQueryKey(entityType, entityPk),
    queryFn: () => getTaskEntityKeys(entityType, entityPk, queryClient),
    staleTime: 0, // Always fetch fresh data
    enabled: shouldFetch,
  });
};

export const useGetTaskEntityStatuses = ({
  entityType,
  entityPk,
  shouldFetch,
}: {
  entityType: ValueOf<typeof RelatedEntityType>;
  entityPk: string | number;
  shouldFetch: boolean;
}) => {
  const queryClient = useQueryClient();
  const { data: taskEntityKeys } = useGetTaskEntityKeys({
    entityType,
    entityPk,
    shouldFetch,
    queryClient,
  });
  const partFileQueries = useMemo(
    () =>
      taskEntityKeys?.[TaskEntityType.PART_FILE]?.map((entityKey) => ({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryClient?
        queryKey: getEntityTaskStatusQueryKey(entityKey),
        queryFn: () => getEntityTaskStatus(entityKey, queryClient),
        staleTime: 0, // Always fetch fresh data
      })),
    [taskEntityKeys, queryClient],
  );
  const componentQueries = useMemo(
    () =>
      taskEntityKeys?.[TaskEntityType.COMPONENT]?.map((entityKey) => ({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryClient?
        queryKey: getEntityTaskStatusQueryKey(entityKey),
        queryFn: () => getEntityTaskStatus(entityKey, queryClient),
        staleTime: 0, // Always fetch fresh data
      })),
    [taskEntityKeys, queryClient],
  );
  const quoteQueries = useMemo(
    () =>
      taskEntityKeys?.[TaskEntityType.QUOTE]?.map((entityKey) => ({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryClient?
        queryKey: getEntityTaskStatusQueryKey(entityKey),
        queryFn: () => getEntityTaskStatus(entityKey, queryClient),
        staleTime: 0, // Always fetch fresh data
      })),
    [taskEntityKeys, queryClient],
  );
  // NOTE: this is a newly generated array each time - not react lifecycle safe
  //  We could use the combine prop in later versions of react query to make this
  //  lifecycle safe, but its not available in v3
  //  https://tanstack.com/query/latest/docs/framework/react/reference/useQueries
  return useQueries([
    ...(partFileQueries ?? []),
    ...(componentQueries ?? []),
    ...(quoteQueries ?? []),
  ]);
};

/**
 * Given a descriptor of a RelatedEntity (quote, part, etc.) use the cache of task entity keys
 * to look up the status of all related task entities, monitoring them for any tasks
 * that are queued or in progress.
 * This essentially enables you to babysit loading for any parent resource without needing
 * to gather explicitly all the entities related to it (like part files, components) that
 * influence its loading state.
 *
 * NOTE: We are ramping up usage of this tasks tracking system over time where the backend
 *  only emits a percentage of messages. As a result, the auto loading starte is not reliable
 *  unless we are sure all task status messages are being emitted. We know this is true if
 *  runes is enabled (as of June 2025)
 * TODO: update this as we ramp up usage of this system:
 *  https://paperlessparts.atlassian.net/browse/PP-22951
 */
export const useAutoIsLoading = (descriptor: AutoWatchTasksDescriptor) => {
  const isRunesEnabled = useFeatureFlagValue(
    featureFlagKeys.isEnhancedPartbotEnabled,
  );
  const modernFileStatusingPercentage = useFeatureFlagValue(
    featureFlagKeys.modernFileStatusingPercentage,
  );
  const shouldFetch = isRunesEnabled || modernFileStatusingPercentage === 100;

  const statuses = useGetTaskEntityStatuses({
    entityType: descriptor.sourceEntityType,
    entityPk: descriptor.sourceEntityPk,
    shouldFetch,
  });

  const optimizedDescriptor = useMemo(
    () =>
      getOptimizedDescriptor({
        ...descriptor,
        // add loading statuses
        statuses: [TaskStatus.QUEUED, TaskStatus.IN_PROGRESS],
      }),
    [descriptor],
  );

  if (!shouldFetch) {
    return false;
  }

  // NOTE: since the statuses are a newly generated array each time, there is no point
  //  in memoizing this.
  return isAnyRelatedEntityLoading({
    relatedEntityStatuses: statuses,
    descriptor: optimizedDescriptor,
  });
};
